(function () {
  'use strict';

  const ALGOLIA_CDN    = 'https://cdn.jsdelivr.net/npm/algoliasearch@4/dist/algoliasearch-lite.umd.js';

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing._gdlLoaded) return resolve();
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.addEventListener('load', () => { script._gdlLoaded = true; resolve(); });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function formatMoney(price, currency) {
    if (price == null || price === '') return '';
    const num = parseFloat(price);
    if (isNaN(num)) return '';
    try {
      const settings = window.theme && window.theme.settings;
      const moneyFormat = settings && (settings.show_currency_codes ? settings.money_with_currency_format : settings.money_format);
      if (moneyFormat && typeof window.formatPrice === 'function') {
        return window.formatPrice(moneyFormat, num);
      }
    } catch (e) {
      // fall through to generic formatting below
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    } catch (e) {
      return num.toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function toTitleCase(str) {
    return String(str || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  function marketPricingAttr(currency) {
    return `market_pricing_${(currency || 'EUR').toLowerCase()}`;
  }

  function marketPricing(hit, currency) {
    return hit[marketPricingAttr(currency)] || hit.market_pricing_eur;
  }

  function isIndexableCollection(hit) {
    const noIndex = hit?.meta?.custom?.no_index;
    return noIndex !== true && noIndex !== 'true';
  }

  function prefixUrl(base, url) {
    if (!url || !base || url.startsWith('http') || url.startsWith(base)) return url || '';
    return base + url;
  }

  function editDistance(a, b) {
    const row = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
      let prev = row[0];
      row[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const tmp = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
        prev = tmp;
      }
    }
    return row[b.length];
  }

  /**
   * "Forse cercavi": Algolia trova già i prodotti con errori di battitura (jans → Jeans) e lo segnala
   * in _rankingInfo.nbTypos. Se il primo risultato ha un typo, ricostruiamo la query sostituendo
   * ogni parola con la parola evidenziata (<em>) nei titoli che le somiglia di più.
   * Richiede getRankingInfo: true e attributesToHighlight: ['title'] nella query.
   */
  function didYouMean(query, hits) {
    if (!hits.length || !(hits[0]._rankingInfo?.nbTypos > 0)) return '';

    const matched = new Set();
    hits.forEach(hit => {
      if (!(hit._rankingInfo?.nbTypos > 0)) return;
      const value = hit._highlightResult?.title?.value || '';
      (value.match(/<em>(.*?)<\/em>/g) || []).forEach(em => {
        matched.add(em.replace(/<\/?em>/g, '').toLowerCase());
      });
    });

    const words = query.trim().toLowerCase().split(/\s+/);
    const corrected = words.map(word => {
      if (matched.has(word)) return word;
      const maxTypos = word.length >= 8 ? 2 : 1;
      let best = word;
      let bestDistance = maxTypos + 1;
      matched.forEach(candidate => {
        const distance = editDistance(word, candidate);
        if (distance < bestDistance) {
          best = candidate;
          bestDistance = distance;
        }
      });
      return best;
    }).join(' ');

    return corrected !== words.join(' ') ? corrected : '';
  }

  function shopifyImageUrl(url, width) {
    if (!url) return url;
    try {
      const u = new URL(url);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  const RECENT_KEY = 'gdl_recent_products';
  const RECENT_MAX = 6;

  class AlgoliaSearch extends HTMLElement {
    connectedCallback() {
      this.appId            = this.dataset.appId;
      this.apiKey           = this.dataset.apiKey;
      this.indexName        = this.dataset.index;
      this.collectionsIndex = this.dataset.collectionsIndex || '';
      this.searchUrl        = this.dataset.searchUrl || '/search';
      this.currency         = this.dataset.currency || 'USD';
      this.labelProducts    = this.dataset.labelProducts        || 'Products';
      this.labelCollections = this.dataset.labelCollections     || 'Collections';
      this.labelPopular     = this.dataset.labelPopularSearches || 'Popular searches';
      this.labelDidYouMean  = this.dataset.labelDidYouMean || 'Forse cercavi';
      this.labelNoResults   = this.dataset.labelNoResults       || 'No results found';
      this.labelViewAll     = this.dataset.labelViewAll         || 'View results for "__QUERY__"';
      this.badgeRadius        = this.dataset.badgeRadius;
      this.labelPopularProds      = this.dataset.labelPopularProducts  || 'Prodotti più cercati';
      this.labelRecentProds       = this.dataset.labelRecentProducts   || 'Hai cercato questi prodotti';
      this.newArrivalsIndex       = this.dataset.newArrivalsIndex || this.indexName;
      this.urlPrefix              = this.dataset.urlPrefix || '';
      if (this.urlPrefix && !this.urlPrefix.startsWith('/')) this.urlPrefix = '/' + this.urlPrefix;
      this.labelNewArrivals       = this.dataset.labelNewArrivals      || 'Nuovi Arrivi';
      this.labelPopularCategories = this.dataset.labelPopularCategories || 'Categorie più cercate';
      this.labelRecentlySearched  = this.dataset.labelRecentlySearched  || 'Hai cercato questi prodotti';

      try { this.popularSearches  = JSON.parse(this.dataset.popularSearches  || '[]'); } catch(e) { this.popularSearches  = []; }
      try { this.preSearchGroups  = JSON.parse(this.dataset.preSearchGroups  || '[]'); } catch(e) { this.preSearchGroups  = []; }

      this.input            = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('.js-algolia-results');
      this.clearBtn         = this.querySelector('.js-algolia-clear');
      this.submitBtn        = this.querySelector('.js-algolia-submit');
      this.errorContainer   = this.querySelector('.js-algolia-error');
      this.preSearchEl      = this.querySelector('.js-algolia-pre-search');

      this.timeout            = null;
      this.isOpen             = false;
      this.algoliaInitialized = false;
      this.drawer             = this.closest('.js-drawer');
      this.client             = null;
      this.popularProducts    = null;
      this.popularCollections = null;
      this.newArrivals        = null;
      this.currentHitsMap     = {};
      this.currentQueryID     = '';

      if (!this.indexName) {
        console.warn('[AlgoliaSearch] data-index attribute is required');
        return;
      }

      this.bindEvents();
      this._observeDrawerOpen();
    }

    _observeDrawerOpen() {
      new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || this.input.value.trim()) return;
          this.initAlgolia();
          setTimeout(() => {
            if (!this.input.value.trim()) {
              this.showPreSearch();
              this.input.focus({ preventScroll: true });
            }
          }, 50);
        });
      }, { threshold: 0.1 }).observe(this.input);
    }

    async initAlgolia() {
      if (this.algoliaInitialized) return;
      this.algoliaInitialized = true;
      try {
        if (!window.algoliasearch) await loadScript(ALGOLIA_CDN);
        this.client = window.algoliasearch(this.appId, this.apiKey);

        // Load Insights and store config for the Web Pixel
        window.GdlAlgoliaInsights?.ensureInsights(this.appId, this.apiKey, this.indexName);

        this.fetchPopularProducts().then(hits => {
          this.popularProducts = hits;
          this._refreshPreSearch();
        });

        if (this.collectionsIndex) {
          this.fetchPopularCollections().then(hits => {
            this.popularCollections = hits;
            this._refreshPreSearch();
          });
        }

      } catch (err) {
        console.error('[AlgoliaSearch] Failed to initialize:', err);
      }
    }

    _trackClick(objectID, queryID, position) {
      window.GdlAlgoliaInsights?.trackClick({
        index:    this.indexName,
        objectID,
        handle:   this.currentHitsMap[objectID]?.handle || '',
        queryID,
        position,
        appId:    this.appId,
        apiKey:   this.apiKey,
      });
    }

    _refreshPreSearch() {
      if (this.preSearchEl && !this.preSearchEl.classList.contains('hidden')) {
        this.preSearchEl.innerHTML = this.renderPreSearch(this.popularProducts || [], this.getRecentProducts());
      }
    }

    bindEvents() {
      const getDrawerContent = () => document.querySelector('.drawer__content--search');
      const getSuggestions   = () => document.querySelector('.search-drawer__suggestions');

      // Lazy init + show pre-search when input is focused and empty
      this.input.addEventListener('focus', () => {
        this.initAlgolia();
        if (!this.input.value.trim()) this.showPreSearch();
      });

      this.input.addEventListener('input', () => {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          const query = this.input.value.trim();

          if (!query) {
            this.close();
            const dc = getDrawerContent();
            const ss = getSuggestions();
            if (window.innerWidth < 990 && dc) dc.style.height = 'auto';
            if (ss) ss.style.display = 'block';
            return;
          }

          this.isOpen = true;
          if (this.preSearchEl) this.preSearchEl.classList.add('hidden');

          const dc = getDrawerContent();
          const ss = getSuggestions();
          if (window.innerWidth <= 990 && dc) dc.style.height = '100%';
          if (ss) ss.style.display = 'none';

          this.search(query);
        }, 300);
      });

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (!this.input.value && this.drawer) {
            this.drawer.parentElement?.parentElement?.toggleDrawer?.();
            return;
          }
          this.close();
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          window.location.href = `${this.searchUrl}?q=${encodeURIComponent(this.input.value.trim())}`;
        }
      });

      // Lente: invia la ricerca come Invio; a campo vuoto riporta il focus sull'input
      this.submitBtn?.addEventListener('click', () => {
        const query = this.input.value.trim();
        if (!query) {
          this.input.focus();
          return;
        }
        window.location.href = `${this.searchUrl}?q=${encodeURIComponent(query)}`;
      });

      this.clearBtn.addEventListener('click', () => {
        this.close();
        const dc = getDrawerContent();
        const ss = getSuggestions();
        if (dc) dc.style.height = 'auto';
        if (ss) ss.style.display = 'block';
      });

      // Track product clicks to save to recent (event delegation)
      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-product-id]');
        if (!card) return;
        const id = card.dataset.productId;
        if (!id) return;

        // Hit from live search results
        if (this.currentHitsMap[id]) {
          this.saveRecentProduct(this.currentHitsMap[id]);
          const position = parseInt(card.dataset.position, 10) || 1;
          this._trackClick(id, this.currentQueryID, position);
          return;
        }
        // Hit from pre-search popular cards (read from data attributes)
        if (card.dataset.productTitle !== undefined) {
          this.saveRecentFromAttrs(card.dataset);
        }
      });
    }

    async search(query) {
      if (!this.client) {
        setTimeout(() => this.isOpen && this.search(query), 200);
        return;
      }

      try {
        const queries = [
          {
            indexName: this.indexName,
            query,
            params: {
              hitsPerPage: 6,
              clickAnalytics: true,
              distinct: true,
              attributesToRetrieve: [...new Set([
                'objectID', 'title', 'handle', 'image', 'images',
                'price', 'price_min', 'price_max', 'compare_at_price', 'url', 'vendor',
                'meta', 'tags', 'market_pricing_eur', marketPricingAttr(this.currency),
              ])],
              attributesToHighlight: ['title'],
              getRankingInfo: true,
            },
          },
        ];

        if (this.collectionsIndex) {
          queries.push({
            indexName: this.collectionsIndex,
            query,
            params: {
              hitsPerPage: 12,
              attributesToRetrieve: ['objectID', 'title', 'handle', 'image', 'products_count', 'url', 'meta'],
              attributesToHighlight: ['title'],
            },
          });
        }

        const { results } = await this.client.multipleQueries(queries);
        if (!this.isOpen) return;

        const seenHandles = new Set();
        const productHits = (results[0]?.hits || []).filter(hit => {
          if (!hit.handle || seenHandles.has(hit.handle)) return false;
          seenHandles.add(hit.handle);
          return true;
        });
        const collectionHits = this.collectionsIndex
          ? (results[1]?.hits || []).filter(isIndexableCollection).slice(0, 3)
          : [];

        this.currentQueryID = results[0]?.queryID || '';

        // Build lookup map for click tracking
        this.currentHitsMap = {};
        productHits.forEach(hit => { this.currentHitsMap[hit.objectID] = hit; });

        this.resultsContainer.innerHTML =
          this.renderDidYouMean(didYouMean(query, results[0]?.hits || [])) +
          this.renderResults(productHits, collectionHits, query);
        this.open();

      } catch (err) {
        console.error('[AlgoliaSearch] Search error:', err);
        if (this.isOpen) this.errorContainer.classList.remove('hidden');
      }
    }

    // ─── Pre-search ─────────────────────────────────────────────────────────────

    showPreSearch() {
      console.log('[GDL] showPreSearch called', {
        preSearchEl: !!this.preSearchEl,
        popularProducts: this.popularProducts?.length ?? 'null',
        popularCollections: this.popularCollections?.length ?? 'null',
        preSearchGroups: this.preSearchGroups.length,
        recentProducts: this.getRecentProducts().length,
      });
      if (!this.preSearchEl) return;
      const popular = this.popularProducts || [];
      const recent  = this.getRecentProducts();
      this.preSearchEl.innerHTML = this.renderPreSearch(popular, recent);
      this.preSearchEl.classList.remove('hidden');
      this.resultsContainer.classList.add('hidden');
      this.errorContainer.classList.add('hidden');
      this.classList.add('is-open');
      this.clearBtn.removeAttribute('disabled');
      this.input.setAttribute('aria-expanded', 'true');
    }

    async fetchPopularProducts() {
      if (!this.client) return [];
      try {
        const { results } = await this.client.multipleQueries([{
          indexName: this.indexName,
          query: '',
          params: {
            hitsPerPage: 6,
            attributesToRetrieve: [...new Set([
              'objectID', 'title', 'handle', 'image', 'images', 'url', 'meta',
              'price', 'price_min', 'price_max', 'compare_at_price', 'tags', 'market_pricing_eur', marketPricingAttr(this.currency),
            ])],
            attributesToHighlight: [],
          },
        }]);
        return results[0]?.hits || [];
      } catch (e) {
        return [];
      }
    }

    async fetchPopularCollections() {
      if (!this.client || !this.collectionsIndex) return [];
      try {
        const { results } = await this.client.multipleQueries([{
          indexName: this.collectionsIndex,
          query: '',
          params: {
            hitsPerPage: 12,
            attributesToRetrieve: ['objectID', 'title', 'handle', 'url', 'meta'],
            attributesToHighlight: [],
          },
        }]);
        return (results[0]?.hits || []).filter(isIndexableCollection).slice(0, 6);
      } catch (e) {
        console.error('[GDL] fetchPopularCollections error:', e);
        return [];
      }
    }

    async fetchNewArrivals() {
      if (!this.client) return [];
      try {
        const { results } = await this.client.multipleQueries([{
          indexName: this.newArrivalsIndex,
          query: '',
          params: {
            hitsPerPage: 24,
            attributesToRetrieve: [...new Set([
              'objectID', 'title', 'handle', 'image', 'images', 'url', 'meta',
              'price', 'price_min', 'price_max', 'compare_at_price', 'tags', 'market_pricing_eur', marketPricingAttr(this.currency),
            ])],
            attributesToHighlight: [],
          },
        }]);
        return results[0]?.hits || [];
      } catch (e) {
        console.error('[GDL] fetchNewArrivals error:', e);
        return [];
      }
    }

    getRecentProducts() {
      try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch(e) { return []; }
    }

    saveRecentProduct(hit) {
      this.saveRecentFromAttrs({
        productId:        String(hit.objectID),
        productTitle:     hit.meta?.custom?.sp_fname_plp || hit.title || '',
        productUrl:       hit.url || `/products/${hit.handle}`,
        productImage:     hit.image || '',
        productColor:     hit.meta?.custom?.sp_colore || '',
        productCategory:  hit.meta?.custom?.sp_capsule_label || '',
        productPrice:     String(marketPricing(hit, this.currency)?.price ?? hit.price ?? hit.price_min ?? ''),
        productCompareAt: String(marketPricing(hit, this.currency)?.compare_at_price ?? hit.compare_at_price ?? ''),
        productTags:      JSON.stringify(Array.isArray(hit.tags) ? hit.tags : []),
      });
    }

    saveRecentFromAttrs(ds) {
      let tags = [];
      try { tags = JSON.parse(ds.productTags || '[]'); } catch(e) {}
      const item = {
        objectID:  ds.productId,
        title:     ds.productTitle    || '',
        url:       ds.productUrl      || '',
        image:     ds.productImage    || '',
        color:     ds.productColor    || '',
        category:  ds.productCategory || '',
        price:     ds.productPrice    || '',
        compareAt: ds.productCompareAt || '',
        tags,
      };
      let recent = this.getRecentProducts().filter(r => r.objectID !== item.objectID);
      recent.unshift(item);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, RECENT_MAX))); } catch(e) {}
    }

    renderPreSearch(popular, recent) {
      const hasPopular = popular.length > 0;
      const hasRecent  = recent.length > 0;

      const collections = this.popularCollections || [];
      const hasCollectionsGroup = collections.length > 0;

      const collectionsGroupHtml = hasCollectionsGroup ? `
        <div class="pre-search__group">
          <p class="pre-search__heading">${escapeHtml(this.labelPopularCategories)}</p>
          <ul class="pre-search__links list-unstyled">
            ${collections.map(c => {
              const url = prefixUrl(this.urlPrefix, c.url || `/collections/${c.handle}`);
              return `<li><a href="${escapeHtml(url)}" class="pre-search__link">${escapeHtml(c.title || '')}</a></li>`;
            }).join('')}
          </ul>
        </div>
      ` : '';

      const staticGroupsHtml = this.preSearchGroups.map(g => `
        <div class="pre-search__group">
          <p class="pre-search__heading">${escapeHtml(g.title)}</p>
          <ul class="pre-search__links list-unstyled">
            ${g.links.map(l => `
              <li><a href="${escapeHtml(l.url)}" class="pre-search__link">${escapeHtml(l.title)}</a></li>
            `).join('')}
          </ul>
        </div>
      `).join('');

      const hasLeft = hasCollectionsGroup || this.preSearchGroups.length > 0;

      const leftHtml = hasLeft ? `
        <div class="pre-search__col--links">
          ${collectionsGroupHtml}
          ${staticGroupsHtml}
        </div>
      ` : '';

      const popularHtml = hasPopular ? `
        <div class="pre-search__section">
          <p class="pre-search__heading">${escapeHtml(this.labelPopularProds)}</p>
          <div class="pre-search__grid">
            ${popular.map(h => this.renderProductCard(h)).join('')}
          </div>
        </div>
      ` : '';

      const recentlyViewed = recent.slice(0, 4);
      const hasRecentlyViewed = recentlyViewed.length > 0;
      const recentlyViewedHtml = `
        <div class="pre-search__col--new-arrivals">
          ${hasRecentlyViewed ? `
            <p class="pre-search__heading">${escapeHtml(this.labelRecentlySearched)}</p>
            <div class="pre-search__new-arrivals-scroll">
              <div class="pre-search__new-arrivals-grid">
                ${recentlyViewed.map(item => this.renderRecentProductCard(item)).join('')}
              </div>
            </div>
          ` : ''}
        </div>
      `;

      return `<div class="pre-search">${leftHtml}${popularHtml}${recentlyViewedHtml}</div>`;
    }

    renderPreSearchProductCard(hit) {
      const title    = hit.meta?.custom?.sp_fname_plp || hit.title || '';
      const img      = shopifyImageUrl(hit.image || '', 400);
      const rawUrl   = hit.url || `/products/${hit.handle}`;
      const url      = prefixUrl(this.urlPrefix, rawUrl);
      const color    = toTitleCase(hit.meta?.custom?.sp_colore || '');
      const category = hit.meta?.custom?.sp_capsule_label || '';
      return `
        <a href="${escapeHtml(url)}" class="pre-search__product-card"
           data-product-id="${escapeHtml(String(hit.objectID))}"
           data-product-title="${escapeHtml(title)}"
           data-product-url="${escapeHtml(rawUrl)}"
           data-product-image="${escapeHtml(hit.image || '')}"
           data-product-color="${escapeHtml(color)}"
           data-product-category="${escapeHtml(category)}">
          <div class="pre-search__product-img">
            ${img ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(title)}" loading="lazy" width="200" height="267">` : ''}
          </div>
          <p class="pre-search__product-name">${escapeHtml(title)}</p>
          ${color ? `<p class="pre-search__product-color">${escapeHtml(color)}</p>` : ''}
        </a>
      `;
    }

    renderRecentProductCard(item) {
      const title    = item.title    || '';
      const imageUrl = item.image    || '';
      const url      = prefixUrl(this.urlPrefix, item.url || '');
      const color    = toTitleCase(item.color    || '');
      const category = item.category || '';
      const price    = item.price;
      const compareAt = item.compareAt;
      const fPrice   = formatMoney(price, this.currency);
      const fCompare = (compareAt && parseFloat(compareAt) > parseFloat(price)) ? formatMoney(compareAt, this.currency) : null;

      const badgeShapeClass = this.badgeRadius === '0' ? 'product-card__badge--square' : 'product-card__badge--rounded';
      const tags = Array.isArray(item.tags) ? item.tags : [];
      const badgeLabels = tags
        .filter(t => typeof t === 'string' && t.startsWith('Badge:'))
        .map(t => t.slice(6).trim().toUpperCase());
      const badgesHtml = badgeLabels.length
        ? `<span class="product-card__badges">${badgeLabels.map(b => `<span class="product-card__badge new-in-badge ${badgeShapeClass}">${escapeHtml(b)}</span>`).join('')}</span>`
        : '';

      const priceHtml = fCompare
        ? `<div class="price__sale">
             <div class="price__sale-inner">
               <s>${fCompare}</s>
               <ins>${fPrice}</ins>
             </div>
           </div>`
        : `<div class="price__regular"><span>${fPrice}</span></div>`;

      return `
        <a href="${escapeHtml(url)}" class="predictive-search__card-product product-card" data-product-id="${escapeHtml(String(item.objectID))}">
          <div class="product-card__media">
            ${imageUrl
              ? `<img src="${escapeHtml(shopifyImageUrl(imageUrl, 400))}" alt="${escapeHtml(title)}" loading="lazy" width="300" height="400">`
              : ''
            }
            ${badgesHtml}
          </div>
          <div class="product-card__content">
            <div class="product-card__info product-card__badge--on-start">
              <small class="product-card__category">${escapeHtml(category)}</small>
              <h3 class="product-card__title" role="presentation">
                <span class="product-card__link">${escapeHtml(title)}</span>
              </h3>
              ${color ? `<span class="product-card_variants-color">${escapeHtml(color)}</span>` : ''}
              <span class="product-card__price">
                <div class="price">
                  <div class="price__container">
                    ${priceHtml}
                  </div>
                </div>
              </span>
            </div>
          </div>
        </a>
      `;
    }

    // ─── Search results rendering ────────────────────────────────────────────────

    renderDidYouMean(suggestion) {
      if (!suggestion) return '';

      return `
        <p class="predictive-search__did-you-mean">
          ${escapeHtml(this.labelDidYouMean)}:
          <a href="${this.searchUrl}?q=${encodeURIComponent(suggestion)}">${escapeHtml(suggestion)}</a>
        </p>
      `;
    }

    renderResults(products, collections, query) {
      const hasProducts    = products.length > 0;
      const hasCollections = collections.length > 0;
      const hasLeft        = hasCollections || this.popularSearches.length > 0;

      if (!hasProducts && !hasCollections) {
        return `<div class="predictive-search__message"><p>${escapeHtml(this.labelNoResults)}</p></div>`;
      }

      const viewAllLabel = this.labelViewAll.replace('__QUERY__', escapeHtml(query));

      return `
        <div class="predictive-search__results predictive-search__results--algolia">

          ${hasLeft ? `
          <div class="predictive-search__col">
            ${this.renderPopularSearches(query)}
            ${hasCollections ? this.renderCollectionsColumn(collections) : ''}
          </div>` : ''}

          <div class="predictive-search__col predictive-search__col-product">
            ${hasProducts ? `
            <div class="predictive-search__row">
              <p class="predictive-search__title">${escapeHtml(this.labelProducts)}</p>
              <div class="predictive-search__grid">
                ${products.map((hit, i) => this.renderProductCard(hit, i + 1)).join('')}
              </div>
            </div>` : ''}
          </div>

          <div class="predictive-search__col">
            <div class="predictive-search__row">
              <a href="${this.searchUrl}?q=${encodeURIComponent(query)}" class="button button--text predictive-search__link">
                ${viewAllLabel}
              </a>
            </div>
          </div>

        </div>
      `;
    }

    renderPopularSearches(query) {
      if (!this.popularSearches.length) return '';

      const lq = query.toLowerCase();
      let suggestions = this.popularSearches.filter(s => s.title.toLowerCase().includes(lq));
      if (!suggestions.length) suggestions = this.popularSearches.slice(0, 3);

      const items = suggestions.map(s => `
        <li class="predictive-search__links-item">
          <a href="${escapeHtml(s.url)}" class="predictive-search__links-link">
            ${escapeHtml(s.title)}
          </a>
        </li>
      `).join('');

      return `
        <div class="predictive-search__row">
          <p class="predictive-search__title">${escapeHtml(this.labelPopular)}</p>
          <ul class="predictive-search__links list-unstyled">${items}</ul>
        </div>
      `;
    }

    renderCollectionsColumn(collections) {
      const items = collections.map(hit => {
        const title    = (hit._highlightResult?.title?.value || hit.title || '').replace(/<em>/g, '<mark>').replace(/<\/em>/g, '</mark>');
        const imageUrl = hit.image?.src || hit.image || hit.featured_image || '';
        const url      = prefixUrl(this.urlPrefix, hit.url || `/collections/${hit.handle}`);
        const count    = hit.products_count != null ? hit.products_count : '';

        return `
          <li class="predictive-search__links-item">
            <a href="${escapeHtml(url)}" class="predictive-search__collections-box">
              <div class="predictive-search__collections-img">
                <div class="media">
                  ${imageUrl
                    ? `<img src="${escapeHtml(shopifyImageUrl(imageUrl, 150))}" alt="${escapeHtml(hit.title || '')}" loading="lazy" width="100" height="100">`
                    : ''
                  }
                </div>
              </div>
              <div class="predictive-search__collections-title">
                <span>
                  ${title}
                  ${count !== '' ? `<small>${count}</small>` : ''}
                </span>
              </div>
            </a>
          </li>
        `;
      }).join('');

      return `
        <div class="predictive-search__row">
          <p class="predictive-search__title">${escapeHtml(this.labelCollections)}</p>
          <ul class="predictive-search__links predictive-search__collections list-unstyled">${items}</ul>
        </div>
      `;
    }

    renderProductCard(hit, position = 1) {
      const rawTitle     = hit._highlightResult?.title?.value || hit.title || '';
      const displayTitle = (hit.meta?.custom?.sp_fname_plp || rawTitle).replace(/<em>/g, '<mark>').replace(/<\/em>/g, '</mark>');
      const imageUrl     = hit.image || (Array.isArray(hit.images) ? hit.images[0] : '');
      const productUrl   = prefixUrl(this.urlPrefix, hit.url || `/products/${hit.handle}`);
      const mkt          = marketPricing(hit, this.currency);
      const price        = mkt?.price ?? hit.price ?? hit.price_min;
      const compareAt    = mkt?.compare_at_price ?? hit.compare_at_price;
      const fPrice       = formatMoney(price, this.currency);
      const fCompare     = (compareAt && compareAt > price) ? formatMoney(compareAt, this.currency) : null;
      const spColore     = toTitleCase(hit.meta?.custom?.sp_colore || '');
      const spCategory   = hit.meta?.custom?.sp_capsule_label || '';

      const badgeShapeClass = this.badgeRadius === '0' ? 'product-card__badge--square' : 'product-card__badge--rounded';
      const tags = Array.isArray(hit.tags) ? hit.tags : [];
      const badgeLabels = tags
        .filter(t => typeof t === 'string' && t.startsWith('Badge:'))
        .map(t => t.slice(6).trim().toUpperCase());
      const badgesHtml = badgeLabels.length
        ? `<span class="product-card__badges">${badgeLabels.map(b => `<span class="product-card__badge new-in-badge ${badgeShapeClass}">${escapeHtml(b)}</span>`).join('')}</span>`
        : '';

      const priceHtml = fCompare
        ? `<div class="price__sale">
             <div class="price__sale-inner">
               <s>${fCompare}</s>
               <ins>${fPrice}</ins>
             </div>
           </div>`
        : `<div class="price__regular"><span>${fPrice}</span></div>`;

      return `
        <a href="${escapeHtml(productUrl)}" class="predictive-search__card-product product-card" data-product-id="${escapeHtml(String(hit.objectID))}" data-position="${position}">
          <div class="product-card__media">
            ${imageUrl
              ? `<img src="${escapeHtml(shopifyImageUrl(imageUrl, 400))}" alt="${escapeHtml(hit.title || '')}" loading="lazy" width="300" height="400">`
              : ''
            }
            ${badgesHtml}
          </div>
          <div class="product-card__content">
            <div class="product-card__info product-card__badge--on-start">
              <small class="product-card__category">${escapeHtml(spCategory)}</small>
              <h3 class="product-card__title" role="presentation">
                <span class="product-card__link">${displayTitle}</span>
              </h3>
              ${spColore ? `<span class="product-card_variants-color">${escapeHtml(spColore)}</span>` : ''}
              <span class="product-card__price">
                <div class="price">
                  <div class="price__container">
                    ${priceHtml}
                  </div>
                </div>
              </span>
            </div>
          </div>
        </a>
      `;
    }

    // ─── State ───────────────────────────────────────────────────────────────────

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorContainer.classList.add('hidden');
      this.clearBtn.removeAttribute('disabled');
      this.input.setAttribute('aria-expanded', 'true');
      if (this.preSearchEl) this.preSearchEl.classList.add('hidden');
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorContainer.classList.add('hidden');
      if (this.preSearchEl) this.preSearchEl.classList.add('hidden');
      this.input.value = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.focus();
      this.clearBtn.setAttribute('disabled', '');
    }
  }

  if (!customElements.get('algolia-search')) {
    customElements.define('algolia-search', AlgoliaSearch);
  }
})();
